import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import clsx from "clsx";
import { isProductInBasket } from "~/utils/isProductInBasket";
import styles from "./index.module.scss";
import { getBacket } from "../../../../redux/action-creaters/basket";
import { showPopup } from "/redux/action-creaters/popup";
import { popupTypes } from "/redux/reducers/popupReducer";
import { ApiClient } from "/api/client";
import Button from "../../../common/button";
import CopyArticleButton from "/components/common/copyArticleButton";
import { Select } from "../../../common/select";
import { formatPrice, isMoreThanOneYearAgo } from "../../../../utils";
import {
  CART_GOALS,
  ECOMMERCE_LISTS,
  reachCartGoal,
  useEcommerce,
} from "../../../../utils/tracking";
import {
  fetchElectricsProducts,
  formatSelectedElectrics,
} from "../../../../utils/electricsSelection";

const ACTIVATION_PRICE = 6000;

function productRequiresActivation(prod) {
  if (!prod?.PROPERTIES?.length) return false;
  const row = prod.PROPERTIES.find((p) => p.CODE === "TREBUETSYA_AKTIVATSIYA");
  if (!row) return false;
  const alt = String(row.VALUE_ALT || "").toLowerCase();
  return alt === "trebuetsya";
}

function getName(name = "") {
  const [text, model] = name.split(" для ");
  if (!model) {
    return name;
  }
  // eslint-disable-next-line no-unused-vars
  const [_, dopInfo] = model.split(" (") || "";
  return `${text} ${dopInfo ? ` (${dopInfo}` : ""}`;
}

export default function OfferWithElectric({
  product,
  electric = null,
  isDetail = false,
}) {
  const name = product.NAME;
  const productId = product.ID;
  const catalog = product.CATALOG;
  // eslint-disable-next-line eqeqeq
  const isFarkop = product.IBLOCK_SECTION_ID == "635";

  const { sections } = useSelector((state) => state.sections);
  const { host, abCartNoInstall } = useSelector((state) => state.params);
  const subDomain = host ? host.split(".")[0] : "";
  const isCityWithInstall = [
    "msk",
    "spb",
    "nn",
    "kirov",
    "ulyanovsk",
    "petrozavodsk",
  ].includes(subDomain);

  const dispatch = useDispatch();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { trackAddMany } = useEcommerce();
  const [selectedElectrics, setSelectedElectrics] = useState(electric);
  const isElectricsSelected = Boolean(selectedElectrics);
  const [isInstallationSelected, setIsInstallationSelected] =
    useState(isCityWithInstall);
  const [isActivationSelected, setIsActivationSelected] = useState(false);
  const [installCorrection, setInstallCorrection] = useState(0);
  // Строка общей таблицы для времени самого товара — от неё считаем,
  // сколько добавляют электрика и доп. работы к коррекции по товару.
  const [productTableCorrection, setProductTableCorrection] = useState(null);
  const [offsetMinutes, setOffsetMinutes] = useState(0);
  const canDecrease = offsetMinutes > 0;

  const { basket } = useSelector((state) => state.cart);

  const isInCart = useMemo(() => {
    if (!isProductInBasket(basket, product)) {
      return false;
    }
    if (selectedElectrics?.product) {
      return isProductInBasket(basket, selectedElectrics.product);
    }
    return true;
  }, [basket, product, selectedElectrics]);
  // Roistat А/Б-тест (utils/abTests.ts): в варианте old кнопки «В корзину без установки» нет
  const isShowAddToCart = !isInstallationSelected || abCartNoInstall === "new";

  const quantity = catalog.QUANTITY;

  const isAlailable = catalog.AVAILABLE;

  // eslint-disable-next-line no-unused-vars
  const { user } = useSelector((state) => state.user);

  const router = useRouter();

  const blockRef = useRef(null);
  const [isShowMobileBuy, setIsShowMobileBuy] = useState(false);

  const adminDomains = [
    "spb",
    "msk",
    "nn",
    "kirov",
    "ulyanovsk",
    "petrozavodsk",
  ];
  const isAdminUser =
    user?.IS_ADMIN || user?.IS_MANAGER || Boolean(router.query.isManager);
  const currentSubdomain = useMemo(() => {
    if (!host) return "";
    return host.split(".")[0];
  }, [host]);
  const defaultAdminSubdomain = useMemo(() => {
    return adminDomains.includes(currentSubdomain)
      ? currentSubdomain
      : adminDomains[0];
  }, [currentSubdomain]);
  const [adminSubdomain, setAdminSubdomain] = useState(defaultAdminSubdomain);

  useEffect(() => {
    if (!isAdminUser) return;
    setAdminSubdomain(defaultAdminSubdomain);
  }, [isAdminUser, defaultAdminSubdomain]);

  const effectiveHost = useMemo(() => {
    if (!host) return "";
    if (!isAdminUser) return host;
    const hostParts = host.split(".");
    if (!hostParts.length) return host;
    hostParts[0] = adminSubdomain || hostParts[0];
    return hostParts.join(".");
  }, [host, isAdminUser, adminSubdomain]);

  const getInstallPricePerHour = (sectionId, sections, host) => {
    if (!sections[sectionId]?.UF_INSTALL_PRICE) {
      return 0;
    }
    const subDomain = host.split(".")[0];
    if (
      !["msk", "spb", "kirov", "nn", "ulyanovsk", "petrozavodsk"].includes(
        subDomain,
      )
    ) {
      return 0;
    }

    const sectionHourPrices = {};
    sections[sectionId].UF_INSTALL_PRICE.forEach((item) => {
      const itemArray = item.split(":");
      sectionHourPrices[itemArray[0]] = +itemArray[1];
    });
    return sectionHourPrices[subDomain];
  };

  const prices = useMemo(() => {
    const installPricePerHour = getInstallPricePerHour(
      product.IBLOCK_SECTION_ID,
      sections,
      effectiveHost,
    );
    const getInstallCoefficient = (installProduct) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const raw = +installProduct?.INSTALL?.INSTALL_COEFFICIENT;
      if (!raw) return 1;
      return raw / 100;
    };
    let farkopInstallTime = Number(product.INSTALL?.VREMYA_USTANOVKI ?? 0);
    let farkopInstallCoefficient = getInstallCoefficient(product);
    if (!isFarkop) {
      farkopInstallTime = Number(
        selectedElectrics?.product?.INSTALL?.VREMYA_USTANOVKI ?? 0,
      );
      farkopInstallCoefficient = getInstallCoefficient(
        selectedElectrics?.product,
      );
    }
    farkopInstallTime += offsetMinutes;
    const farkopInstallPrice = Math.round(
      (installPricePerHour / 60) * farkopInstallTime * farkopInstallCoefficient,
    );

    let electricsInstallTime;
    let electricsInstallCoefficient;
    if (isFarkop) {
      electricsInstallTime = Number(
        selectedElectrics?.product?.INSTALL?.VREMYA_USTANOVKI ?? 0,
      );
      electricsInstallCoefficient = getInstallCoefficient(
        selectedElectrics?.product,
      );
    } else {
      electricsInstallTime = Number(product.INSTALL?.VREMYA_USTANOVKI ?? 0);
      electricsInstallCoefficient = getInstallCoefficient(product);
    }
    const electricsInstallPrice = Math.round(
      (installPricePerHour / 60) *
        electricsInstallTime *
        electricsInstallCoefficient,
    );

    const combinedProductPrice = selectedElectrics?.price || 0;
    let combinedInstallPrice = 0;
    let combinedInstallPriceOld = 0;
    let combinedInstallTime = 0;
    if (isInstallationSelected) {
      if (combinedProductPrice) {
        // const combinedInstallTime = product.INSTALL?.FARKOP_EL || 0;
        // combinedInstallPrice = Math.round(
        //   (installPricePerHour / 60) * combinedInstallTime,
        // );
        const combinedInstallCoefficient = isFarkop
          ? farkopInstallCoefficient
          : electricsInstallCoefficient;
        combinedInstallTime = Math.max(
          0,
          farkopInstallTime + electricsInstallTime - 30,
        );
        combinedInstallPrice = Math.round(
          (combinedInstallTime / 60) *
            installPricePerHour *
            combinedInstallCoefficient,
        );
        combinedInstallPriceOld = farkopInstallPrice + electricsInstallPrice;
      } else {
        combinedInstallPrice = isFarkop
          ? farkopInstallPrice
          : electricsInstallPrice;
        combinedInstallTime = isFarkop
          ? farkopInstallTime
          : electricsInstallTime;
      }
    }

    const activationEligibleProduct = isFarkop
      ? selectedElectrics?.product
      : product;
    const activationRequired = Boolean(
      activationEligibleProduct &&
        productRequiresActivation(activationEligibleProduct),
    );
    const activationCharge =
      activationRequired && isInstallationSelected && isActivationSelected
        ? ACTIVATION_PRICE
        : 0;

    return {
      productPrice: product.CATALOG.MIN_PRICE,
      combinedProductPrice,
      farkopInstallPrice,
      electricsInstallPrice,
      combinedInstallPrice,
      combinedInstallPriceOld,
      combinedInstallTime,
      activationRequired,
      activationCharge,
      totalPrice:
        product.CATALOG.MIN_PRICE +
        combinedProductPrice +
        combinedInstallPrice +
        activationCharge,
      totalPriceOld:
        product.CATALOG.MIN_PRICE +
        combinedProductPrice +
        combinedInstallPriceOld +
        activationCharge,
    };
  }, [
    product,
    selectedElectrics,
    isElectricsSelected,
    isInstallationSelected,
    sections,
    effectiveHost,
    isFarkop,
    isActivationSelected,
    offsetMinutes,
  ]);

  useEffect(() => {
    if (!prices.activationRequired || !isInstallationSelected) {
      setIsActivationSelected(false);
      return;
    }
    // по умолчанию активация выключена, даже если доступна
    setIsActivationSelected(false);
  }, [prices.activationRequired, isInstallationSelected]);

  /**
   * Коррекция времени установки, заданная в панели по самому товару
   * (таб «Установка»). Есть только у товара текущей карточки: подобранная
   * электрика приходит из списка каталога, без этих полей.
   *
   * Если значение по товару заполнено, показываем его вместо значения из общей
   * таблицы managers-install-correction. Задано оно на установку одного товара,
   * поэтому электрику и доп. работы к нему добавляем так же, как их добавляет
   * общая таблица: на разницу между её строками для общего времени и для
   * времени самого товара.
   */
  const productInstallCorrection = product.INSTALL?.INSTALL_CORRECTION || null;
  const productInstallTime = Number(product.INSTALL?.VREMYA_USTANOVKI ?? 0);

  const buildCorrectionRows = (correction) =>
    [
      { label: "Тройка", key: "triples" },
      { label: "Суперпост", key: "firstRank" },
      { label: "Стажер", key: "trainee" },
      { label: "Супер двойка", key: "superTwo" },
    ].map(({ label, key }) => {
      const tableValue = Number(correction?.[key]) || 0;
      const productValue = Number(productInstallCorrection?.[key]) || 0;
      // Нет строки для времени самого товара — прибавку не из чего посчитать.
      const addedByTable = productTableCorrection
        ? tableValue - (Number(productTableCorrection[key]) || 0)
        : 0;
      return {
        label,
        value: productValue ? productValue + addedByTable : tableValue,
      };
    });

  const formatInstallTime = (minutes) => {
    const totalMinutes = Math.max(0, Math.round(minutes || 0));
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (!hours) return `${mins} мин`;
    if (!mins) return `${hours} ч`;
    return `${hours} ч ${mins} мин`;
  };

  useEffect(() => {
    if (!isInstallationSelected || !isAdminUser) {
      setInstallCorrection(null);
      return;
    }
    const totalMinutes = prices?.combinedInstallTime ?? 0;
    if (!totalMinutes || totalMinutes <= 0) {
      setInstallCorrection(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const raw = await ApiClient.manager.getManagerInstallCorrection();
      if (!raw || cancelled) return;
      const findByMinutes = (minutes) =>
        raw.find((r) => String(r?.code) === String(minutes))?.data || null;
      if (!cancelled) {
        setInstallCorrection(findByMinutes(totalMinutes));
        setProductTableCorrection(findByMinutes(productInstallTime));
      }
    };

    load();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [
    isInstallationSelected,
    isAdminUser,
    prices?.combinedInstallTime,
    productInstallTime,
  ]);

  const handleElectricsSelect = (electricsProduct) => {
    setSelectedElectrics(electricsProduct);
  };

  const handleOpenElectricsPopup = () => {
    dispatch(
      showPopup(popupTypes.selectElectrics, {
        onSelect: handleElectricsSelect,
        product,
        isDetail,
      }),
    );
  };

  const handleInstallationSelectChange = (value) => {
    const next = value === "yes";
    if (!isCityWithInstall && next && !isAdminUser) {
      dispatch(
        showPopup(popupTypes.installWarning, {
          productId: isDetail ? "" : product?.ID,
          selectedElectricsId: selectedElectrics?.id,
          selectedElectricsCode: selectedElectrics?.product?.CODE,
        }),
      );
    }
    if (isCityWithInstall || isAdminUser) {
      setIsInstallationSelected(next);
    }
  };

  const handleActivationSelectChange = (value) => {
    setIsActivationSelected(value === "yes");
  };

  useEffect(() => {
    const { calcElectricsId, calcElectricsCode } = router.query;
    if (!calcElectricsId && !calcElectricsCode) return;

    let cancelled = false;
    const loadElectrics = async () => {
      try {
        const products = await fetchElectricsProducts(product);
        if (cancelled) return;

        const electricsProduct = products.find((item) => {
          if (calcElectricsCode && item.CODE === calcElectricsCode) {
            return true;
          }
          return String(item.ID) === String(calcElectricsId);
        });
        if (!electricsProduct) return;

        setSelectedElectrics(formatSelectedElectrics(electricsProduct));

        const url = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, url);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    loadElectrics();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [
    router.query.calcProductId,
    router.query.calcElectricsId,
    router.query.calcElectricsCode,
    productId,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      if (!blockRef.current) return;
      if (window.innerWidth > 991) return;

      const rect = blockRef.current.getBoundingClientRect();
      const offsetTop = rect.top + window.scrollY - 150;
      const { scrollY } = window;

      if (scrollY > offsetTop + blockRef.current.offsetHeight) {
        setIsShowMobileBuy(true);
      } else {
        setIsShowMobileBuy(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // проверка при загрузке

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const formatOrderItem = (itemName, article, itemPrice) => {
    if (!itemName) return "";
    const articlePart = article ? `, ${article}` : "";
    return `${itemName}${articlePart} (цена: ${formatPrice(itemPrice)} руб)`;
  };

  const getOrderComposition = () => {
    const items = [
      formatOrderItem(name, product.ARTICLE?.VALUE, prices.productPrice),
    ];

    if (selectedElectrics) {
      items.push(
        formatOrderItem(
          selectedElectrics.name,
          selectedElectrics.product?.ARTICLE?.VALUE,
          selectedElectrics.price,
        ),
      );
    }

    if (isInstallationSelected) {
      items.push(
        `установка (цена: ${formatPrice(prices.combinedInstallPrice)} руб)`,
      );
    }

    if (prices.activationCharge > 0) {
      items.push(
        `активация (цена: ${formatPrice(prices.activationCharge)} руб)`,
      );
    }

    return items.filter(Boolean).join(", ");
  };

  // Заявка на установку — отдельный сценарий, корзину не затрагивает.
  const orderWithInstall = () => {
    dispatch(
      showPopup(popupTypes.orderWithInstall, {
        composition: getOrderComposition(),
      }),
    );
  };

  // «В корзину» доступна при любом состоянии переключателя установки:
  // в корзину уходит товар (и выбранная электрика), сама услуга — нет,
  // поэтому переключатель сбрасывается в «Нет», чтобы цена на карточке совпадала с корзиной.
  const addToCart = async () => {
    if (isInCart) {
      router.push("/personal/cart/");
      return;
    }

    setIsInstallationSelected(false);
    setIsAddingToCart(true);
    if (selectedElectrics?.product) {
      await ApiClient.basket.addProductToBasket(
        selectedElectrics.product.ID,
        selectedElectrics.product.CART_PROPS || null,
      );
    }
    await ApiClient.basket.addProductToBasket(
      product.ID,
      product.CART_PROPS || null,
    );
    await dispatch(getBacket(true));
    await dispatch(showPopup(popupTypes.cart));
    setIsAddingToCart(false);

    reachCartGoal(CART_GOALS.addToCart, user);
    // В корзину уходит и электрика, выбранная вместе с фаркопом, — в метрику
    // отправляем обе позиции одним событием.
    trackAddMany([product, selectedElectrics?.product].filter(Boolean), {
      list: isDetail ? ECOMMERCE_LISTS.detail : ECOMMERCE_LISTS.catalog,
    });
  };

  return (
    <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
      {isAlailable ? (
        <>
          <span itemProp="price" style={{ display: "none" }}>
            {product.CATALOG?.MIN_PRICE}
          </span>
          <span itemProp="priceCurrency" style={{ display: "none" }}>
            RUB
          </span>
          <link itemProp="availability" href="http://schema.org/InStock" />
        </>
      ) : (
        <>
          <link itemProp="availability" href="http://schema.org/OutOfStock" />
        </>
      )}
      {isAdminUser && (
        <div className={styles.AdminDomainSwitch}>
          <div className={styles.AdminDomainSwitchTitle}>
            Стоимость установки по поддоменам(для админов):
          </div>
          {adminDomains.map((domain) => (
            <Button
              key={domain}
              type={adminSubdomain === domain ? "" : "outline"}
              label={domain}
              customStyles={styles.AdminDomainButton}
              onClick={() => {
                setAdminSubdomain(domain);
              }}
            />
          ))}
        </div>
      )}
      {(isAlailable || isAdminUser) && (
        <div className={styles.ElectricsBlock}>
          {Boolean(router.query?.isDebug) && (
            <div>
              стоимость руб в час:{" "}
              {getInstallPricePerHour("635", sections, effectiveHost)}
              <pre>{JSON.stringify(product.INSTALL, null, 2)}</pre>
              {selectedElectrics && (
                <pre>
                  Доп товар:
                  {JSON.stringify(selectedElectrics?.product?.INSTALL, null, 2)}
                </pre>
              )}
            </div>
          )}
          <div className={styles.ElectricsOptions}>
            <div
              className={styles.ElectricsOption}
              onClick={() => {
                setSelectedElectrics(null);
              }}
            >
              <div
                className={clsx(
                  styles.ElectricsOptionImage,
                  !isElectricsSelected && styles.ElectricsOptionImageSelected,
                )}
              >
                <img src={"/noel.jpg"} />
              </div>
              <div className={styles.ElectricsOptionName}>
                {" "}
                {isFarkop ? "Без электрики" : "Без фаркопа"}
              </div>
            </div>

            {selectedElectrics ? (
              <div
                className={styles.ElectricsOption}
                onClick={() => {
                  setSelectedElectrics(null);
                }}
              >
                <div
                  className={clsx(
                    styles.ElectricsOptionImage,
                    isElectricsSelected && styles.ElectricsOptionImageSelected,
                  )}
                >
                  <img
                    src={selectedElectrics.image}
                    alt={selectedElectrics.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <svg
                    width="25"
                    height="30"
                    viewBox="0 0 25 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.2917 0C15.1042 0 17.3961 2.22936 17.4966 5.01739L17.5 5.20917L23.125 5.20833C23.9304 5.20833 24.5833 5.86125 24.5833 6.66667C24.5833 7.43181 23.9941 8.05933 23.2446 8.12017L23.125 8.125H22.1525L20.7965 26.0999C20.6571 27.9472 19.1175 29.375 17.2649 29.375H7.31843C5.46582 29.375 3.92623 27.9472 3.78681 26.0999L2.43 8.125H1.45833C0.693189 8.125 0.065673 7.53574 0.00483433 6.78627L0 6.66667C0 5.90152 0.589259 5.27401 1.33873 5.21317L1.45833 5.20833H7.08333C7.08333 2.45969 9.21251 0.208351 11.9115 0.0136614L12.1007 0.00343482L12.2917 0ZM15.4167 10.8333C14.8773 10.8333 14.4337 11.2432 14.3804 11.7685L14.375 11.875V21.875L14.3804 21.9815C14.4337 22.5068 14.8773 22.9167 15.4167 22.9167C15.956 22.9167 16.3996 22.5068 16.453 21.9815L16.4583 21.875V11.875L16.453 11.7685C16.3996 11.2432 15.956 10.8333 15.4167 10.8333ZM9.16667 10.8333C8.62733 10.8333 8.18372 11.2432 8.13038 11.7685L8.125 11.875V21.875L8.13038 21.9815C8.18372 22.5068 8.62733 22.9167 9.16667 22.9167C9.70601 22.9167 10.1496 22.5068 10.203 21.9815L10.2083 21.875V11.875L10.203 11.7685C10.1496 11.2432 9.70601 10.8333 9.16667 10.8333ZM12.4313 2.92085L12.2917 2.91667C11.0729 2.91667 10.0763 3.86809 10.0042 5.06873L10 5.20917L14.5833 5.20833C14.5833 3.98956 13.6319 2.993 12.4313 2.92085Z"
                      fill="#FF0000"
                    />
                  </svg>
                </div>
                <div className={styles.ElectricsOptionName}>
                  {getName(selectedElectrics.name)}
                  {isAdminUser && (
                    <CopyArticleButton
                      article={selectedElectrics.product?.ARTICLE?.VALUE}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div
                className={clsx(styles.ElectricsOption)}
                onClick={handleOpenElectricsPopup}
              >
                <div
                  className={clsx(
                    styles.ElectricsOptionImage,
                    styles.ElectricsOptionImageAdd,
                  )}
                >
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 80 80"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M40 25V55M25 40H55"
                      stroke="#9DA4AE"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div className={styles.ElectricsOptionName}>
                  {isFarkop ? "Добавить электрику" : "Добавить фаркоп"}
                </div>
              </div>
            )}
            {isAdminUser && isInstallationSelected && (
              <div className={styles.Controls}>
                <Button
                  type="outline"
                  label="−30 мин"
                  isDisabled={!canDecrease}
                  onClick={() => {
                    if (!canDecrease) return;
                    setOffsetMinutes((v) => Math.max(0, v - 30));
                  }}
                />
                <Button
                  type="outline"
                  label="+30 мин"
                  onClick={() => setOffsetMinutes((v) => v + 30)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Price Summary with Installation */}
      {(isAlailable || isAdminUser) && (
        <div className={styles.PriceSummary}>
          <div className={styles.PriceSummaryRow}>
            <span> {isFarkop ? "Цена фаркопа  " : "Цена электрики"}</span>
            <span>{formatPrice(prices.productPrice)} руб</span>
          </div>
          {prices.combinedProductPrice > 0 && (
            <div className={styles.PriceSummaryRow}>
              <span>{!isFarkop ? "Цена фаркопа  " : "Цена электрики"}</span>
              <span>{formatPrice(prices.combinedProductPrice)} руб</span>
            </div>
          )}
          <div className={styles.PriceSummaryRow}>
            <span>Нужна установка?</span>
            <div className={styles.InstallationSelect}>
              <Select
                selected={isInstallationSelected ? ["yes"] : ["no"]}
                options={[
                  {
                    name: "Да",
                    value: "yes",
                  },
                  {
                    name: "Нет",
                    value: "no",
                  },
                ]}
                placeholder=""
                onChange={handleInstallationSelectChange}
                type={"small"}
              />
            </div>
          </div>
          {isInstallationSelected && (
            <>
              {selectedElectrics ? (
                <div className={styles.PriceSummaryRow}>
                  <span>
                    {isFarkop
                      ? "Установка фаркопа с электрикой "
                      : "Установка электрики с фаркопом"}
                  </span>
                  <div className={styles.PriceSummaryValue}>
                    {prices.combinedInstallPriceOld && (
                      <span className={styles.PriceSummaryOld}>
                        {formatPrice(prices.combinedInstallPriceOld)} руб
                      </span>
                    )}
                    <span className={styles.PriceSummaryNew}>
                      {formatPrice(prices.combinedInstallPrice)} руб
                    </span>
                  </div>
                </div>
              ) : (
                <div className={styles.PriceSummaryRow}>
                  <span>
                    {isFarkop ? "Установка фаркопа " : "Установка электрики"}
                  </span>
                  <span>
                    {formatPrice(
                      isFarkop
                        ? prices.farkopInstallPrice
                        : prices.electricsInstallPrice,
                    )}{" "}
                    руб
                  </span>
                </div>
              )}
            </>
          )}
          {isInstallationSelected && prices.activationRequired && (
            <div className={styles.PriceSummaryRow}>
              <span>Нужна активация?</span>
              <div className={styles.InstallationSelect}>
                <Select
                  selected={isActivationSelected ? ["yes"] : ["no"]}
                  options={[
                    {
                      name: "Да",
                      value: "yes",
                    },
                    {
                      name: "Нет",
                      value: "no",
                    },
                  ]}
                  placeholder=""
                  onChange={handleActivationSelectChange}
                  type={"small"}
                />
              </div>
            </div>
          )}
          {isInstallationSelected &&
            prices.activationRequired &&
            isActivationSelected && (
              <div className={styles.PriceSummaryRow}>
                <span>Активация</span>
                <span>{formatPrice(ACTIVATION_PRICE)} руб</span>
              </div>
            )}
          <div className={styles.PriceSummarySeparator} />
          <div className={styles.PriceSummaryRow}>
            <span>Стоимость:</span>
            <div className={styles.PriceSummaryTotalValue}>
              {prices.totalPriceOld > prices.totalPrice && (
                <span className={styles.PriceSummaryTotalOld}>
                  {formatPrice(prices.totalPriceOld)} руб
                </span>
              )}
              <span className={styles.PriceSummaryTotalNew}>
                {formatPrice(prices.totalPrice)} руб
              </span>
            </div>
          </div>
          {isAdminUser && isInstallationSelected && (
            <div className={styles.InstallTimeWrapper}>
              {installCorrection ? (
                <div className={styles.InstallTimeTable}>
                  {[
                    {
                      label: "Тип",
                      value: "Время, мин",
                      always: true,
                    },
                    offsetMinutes && {
                      label: "Установка товара",
                      value:
                        Math.round(prices.combinedInstallTime || 0) -
                        Math.round(offsetMinutes || 0),
                    },
                    offsetMinutes && {
                      label: "Доп работы",
                      value: Math.round(offsetMinutes || 0),
                    },
                    {
                      label: offsetMinutes
                        ? "Общее время работы"
                        : "Время установки",
                      value: Math.round(prices.combinedInstallTime || 0),
                      isBold: true,
                    },
                    ...buildCorrectionRows(installCorrection),
                  ]
                    .filter((row) => row.always || row.value > 0)
                    .map((row) => (
                      <div
                        key={row.label}
                        className={clsx(
                          styles.InstallTimeTableRow,
                          row.isBold && styles.InstallTimeTableRowBold,
                        )}
                      >
                        <div>{row.label}</div>
                        <div>{row.value}</div>
                      </div>
                    ))}
                </div>
              ) : (
                <span>{formatInstallTime(prices.combinedInstallTime)}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.Body} ref={blockRef}>
        {isAlailable ? (
          <>
            <div className={styles.Buttons}>
              {isInstallationSelected && (
                <Button
                  test="orderWithInstallButton"
                  onClick={orderWithInstall}
                  label={"Заказать с установкой"}
                  style={{ width: "100%" }}
                />
              )}
              {isShowAddToCart && (
                <Button
                  test="addToCartButton"
                  type={isInCart || isInstallationSelected ? "outlineGray" : ""}
                  customStyles={
                    isInstallationSelected && !isInCart
                      ? styles.SecondaryButton
                      : undefined
                  }
                  onClick={addToCart}
                  label={
                    // eslint-disable-next-line no-nested-ternary
                    isInCart
                      ? "В корзине"
                      : isInstallationSelected
                        ? "В корзину без установки"
                        : "В корзину"
                  }
                  isLoading={isAddingToCart}
                  style={{ width: "100%" }}
                />
              )}
            </div>
            {isDetail && (
              <>
                <span className={styles.Separator} />

                <div
                  className={styles.Consultation}
                  onClick={() => {
                    dispatch(showPopup(popupTypes.backCall));
                  }}
                >
                  <div className={styles.ConsultationIcon}>
                    <svg
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.37732 9.78375C8.93966 9.78375 8.5315 10.0195 8.312 10.3989C8.14695 10.6843 7.7819 10.7818 7.49655 10.6168C7.21121 10.4518 7.11373 10.0867 7.27871 9.80133C7.71081 9.05418 8.51499 8.59005 9.37732 8.59005C10.2396 8.59005 11.0438 9.05418 11.4759 9.80126C11.641 10.0866 11.5435 10.4517 11.2582 10.6167C11.1641 10.6712 11.0613 10.697 10.9599 10.6971C10.7538 10.6971 10.5533 10.5903 10.4427 10.399C10.2232 10.0194 9.81492 9.78375 9.37732 9.78375ZM12.5166 16.1123C12.0789 16.1123 11.6707 15.8766 11.4512 15.4971C11.2861 15.2118 10.921 15.1143 10.6357 15.2793C10.3504 15.4443 10.2529 15.8094 10.4179 16.0948C10.85 16.8419 11.6542 17.306 12.5166 17.306C13.3788 17.306 14.183 16.8419 14.6152 16.0948C14.7802 15.8095 14.6828 15.4444 14.3974 15.2794C14.1121 15.1143 13.747 15.2118 13.582 15.4971C13.3625 15.8766 12.9542 16.1123 12.5166 16.1123ZM25 10.2954V13.017C25 14.0321 24.2013 14.8873 23.1861 14.8873H22.0839C21.7219 16.0146 21.1474 17.106 20.3738 18.0816C19.1027 19.6845 17.3534 20.8489 15.4082 21.4359C15.3208 22.4179 14.4937 23.1765 13.4897 23.1765H11.5435C10.4814 23.1765 9.61724 22.3156 9.61724 21.2534C9.61724 20.191 10.4814 19.3303 11.5435 19.3303H13.4897C14.1888 19.3303 14.8021 19.7151 15.1397 20.2738C18.8183 19.1356 21.3452 15.7488 21.3452 11.8519C21.3452 8.05908 18.9412 4.81717 15.5769 3.57009C14.7025 4.64489 13.6031 5.53906 12.3745 6.17135C10.9694 6.89436 9.38588 7.27659 7.79529 7.27659C7.61074 7.27659 7.42281 7.27142 7.2368 7.26121C6.90769 7.24324 6.65544 6.9618 6.67347 6.63269C6.69151 6.30358 6.97188 6.04934 7.30199 6.0693C7.46645 6.07831 7.63243 6.08276 7.79536 6.08276C10.3153 6.08276 12.6462 5.04489 14.3162 3.20723C13.7574 3.09105 13.1797 3.02745 12.588 3.02274C12.5845 3.02241 12.5788 3.02195 12.574 3.02195C12.5719 3.02208 12.5694 3.02042 12.5672 3.02042C12.5626 3.02029 12.5574 3.01737 12.5528 3.01737C12.5505 3.01737 12.5485 3.01737 12.5462 3.01737C12.5416 3.01737 12.5363 3.01737 12.5316 3.01737C12.5293 3.01737 12.5274 3.01737 12.5249 3.01737C12.5221 3.01737 12.5193 3.01737 12.5166 3.01737C7.64841 3.01724 3.71353 6.98369 3.71353 11.8518V14.2611C3.71353 14.5907 3.42069 14.8873 3.09111 14.8873H1.84702C0.831897 14.8873 0 14.0321 0 13.017V10.2954C0 9.4933 0.516313 8.78886 1.28137 8.54257C1.59516 8.44131 1.93369 8.61392 2.03468 8.92778C2.13568 9.24144 1.96174 9.57778 1.64794 9.67878C1.38223 9.76432 1.19357 10.0178 1.19357 10.2954V13.017C1.19357 13.3739 1.48999 13.6936 1.84695 13.6936H2.51989V11.8518C2.51989 6.32546 6.99025 1.82361 12.5166 1.82361C12.5197 1.82361 12.523 1.82361 12.5261 1.82361C12.5285 1.82361 12.5311 1.82361 12.5336 1.82361C12.536 1.82361 12.5385 1.82361 12.5409 1.82361C12.5439 1.82361 12.5473 1.82361 12.5504 1.82361C12.5529 1.82361 12.5556 1.82361 12.5578 1.82361C12.5602 1.82361 12.5627 1.82361 12.5651 1.82361C12.5682 1.82361 12.5716 1.82361 12.5748 1.82361C12.5775 1.82361 12.5798 1.82361 12.5824 1.82361C12.5849 1.82361 12.5879 1.82361 12.5905 1.82361C12.5934 1.82361 12.5962 1.82361 12.5991 1.82361C12.6017 1.82361 12.6039 1.82361 12.6065 1.82361C12.6097 1.82361 12.6122 1.82361 12.6152 1.82361C12.6179 1.82361 12.6207 1.82361 12.6235 1.82361C12.6259 1.82361 12.6285 1.82361 12.6309 1.82361C12.6342 1.82361 12.6369 1.82361 12.6403 1.82361C12.6427 1.82361 12.6458 1.82361 12.648 1.82361C12.6503 1.82361 12.6529 1.82347 12.6552 1.82361C12.6582 1.82361 12.6617 1.82361 12.6649 1.82361C12.6674 1.82361 12.6697 1.82361 12.6723 1.82361C12.6745 1.82361 12.6773 1.82732 12.6794 1.82725C12.6825 1.82739 12.6856 1.83083 12.6889 1.83097C12.6912 1.8309 12.6942 1.8309 12.6964 1.8311C12.6985 1.8311 12.7015 1.83103 12.7036 1.83123C12.7067 1.83117 12.7101 1.83136 12.7133 1.83136C12.7158 1.8315 12.718 1.83143 12.7206 1.83156C12.7227 1.83156 12.7257 1.83163 12.7278 1.8317C12.7308 1.83176 12.7348 1.83196 12.7379 1.8319C12.7404 1.83203 12.7426 1.83203 12.745 1.83209C12.7473 1.83209 12.7497 1.83223 12.752 1.83223C12.755 1.83243 12.7588 1.83236 12.7619 1.83249C12.7643 1.83243 12.7666 1.83262 12.769 1.83262C12.7714 1.83282 12.7737 1.83282 12.7761 1.83282C12.7792 1.83296 12.7826 1.83296 12.7858 1.83309C12.7883 1.83322 12.7905 1.83322 12.793 1.83329C12.7953 1.83342 12.7979 1.83342 12.8001 1.83342C12.8032 1.83362 12.8056 1.83349 12.8088 1.83362C12.8113 1.83375 12.814 1.83362 12.8166 1.83362C12.8192 1.83375 12.8213 1.83335 12.8239 1.83349C12.8266 1.83355 12.829 1.83249 12.8317 1.83302C12.8345 1.83316 12.8375 1.8315 12.8404 1.83183C12.843 1.8319 12.845 1.82911 12.8477 1.82904C12.8527 1.82924 12.8578 1.82367 12.8629 1.82367C12.8653 1.82367 12.8686 1.82367 12.871 1.82367C13.7829 1.88999 14.6823 2.02003 15.5487 2.29476C15.5608 2.29861 15.5727 2.31253 15.5845 2.31711C19.6149 3.61591 22.5388 7.40981 22.5388 11.8665C22.5388 12.4818 22.4845 13.0969 22.3774 13.6937H23.1861C23.543 13.6937 23.8063 13.3739 23.8063 13.017V10.2954C23.8063 10.0179 23.6308 9.76439 23.3651 9.67885C23.0514 9.57785 22.8891 9.24158 22.9901 8.92785C23.091 8.61412 23.4206 8.44178 23.7343 8.54257C24.4995 8.78893 25 9.4933 25 10.2954ZM14.2223 21.2612C14.2223 21.2118 14.2174 21.1747 14.208 21.128C14.2067 21.1223 14.2055 21.1113 14.2044 21.1056C14.1307 20.7788 13.8383 20.5239 13.4898 20.5239H11.5436C11.1396 20.5239 10.8109 20.8493 10.8109 21.2532C10.8109 21.6575 11.1396 21.9828 11.5436 21.9828H13.4898C13.8936 21.9828 14.2223 21.6652 14.2223 21.2612ZM13.775 10.6167C14.0603 10.7818 14.4254 10.6843 14.5905 10.399C14.81 10.0194 15.2182 9.78375 15.6558 9.78375C16.0934 9.78375 16.5017 10.0195 16.7212 10.399C16.8318 10.5903 17.0323 10.6971 17.2384 10.6971C17.3398 10.697 17.4426 10.6712 17.5367 10.6167C17.822 10.4517 17.9195 10.0866 17.7544 9.80126C17.3223 9.05418 16.5181 8.59005 15.6558 8.59005C14.7936 8.59005 13.9894 9.05418 13.5572 9.80126C13.3922 10.0866 13.4897 10.4517 13.775 10.6167Z"
                        fill="#1247A5"
                      />
                    </svg>
                  </div>
                  <span className={styles.ConsultationText}>
                    Нужна консультация?
                  </span>
                  <svg
                    width="8"
                    height="14"
                    viewBox="0 0 8 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 13L7 7L1 1"
                      stroke="#9DA4AE"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </>
            )}
          </>
        ) : (
          <div className={styles.ButtonsEmpty}>
            <Button
              type="outline"
              label={"Подобрать аналог"}
              onClick={() => {
                dispatch(
                  showPopup(popupTypes.similarProduct, {
                    name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                    id: productId,
                  }),
                );
              }}
            />

            {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
              <div
                className={styles.Subscribe}
                onClick={() => {
                  dispatch(
                    showPopup(popupTypes.subscribeProduct, {
                      name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                      id: productId,
                    }),
                  );
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                </svg>
              </div>
            )}
          </div>
        )}
      </div>

      {isDetail && isShowMobileBuy && (
        <div
          className={clsx(
            styles.BodyMobile,
            isAlailable &&
              isInstallationSelected &&
              isShowAddToCart &&
              styles.BodyMobileTwoActions,
          )}
        >
          <div className={styles.BodyMobilePrice}>
            <div className={styles.Qty}>
              {quantity > 0 ? (
                <span className={styles.InStock}>В наличии: {quantity} шт</span>
              ) : (
                <span className={styles.OutOfStock}>
                  {isMoreThanOneYearAgo(product.TIMESTAMP_X)
                    ? "Нет в наличии"
                    : "Распродано"}
                </span>
              )}
            </div>
            <div className={styles.Price}>
              <div className={styles.Value}>
                {isAlailable && (
                  <>
                    <span>
                      <span itemProp="price">
                        {formatPrice(prices.totalPrice)}
                      </span>{" "}
                      руб
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.BodyMobileButtons}>
            {!isAlailable ? (
              <div className={styles.ButtonsEmpty}>
                <Button
                  type="outline"
                  label={"Подобрать аналог"}
                  onClick={() => {
                    dispatch(
                      showPopup(popupTypes.similarProduct, {
                        name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                        id: productId,
                      }),
                    );
                  }}
                />

                {!isMoreThanOneYearAgo(product.TIMESTAMP_X) && (
                  <div
                    className={styles.Subscribe}
                    onClick={() => {
                      dispatch(
                        showPopup(popupTypes.subscribeProduct, {
                          name: `${product.NAME} (арт ${product.ARTICLE?.VALUE}) (id ${product.ID})`,
                          id: productId,
                        }),
                      );
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path d="M439.39 362.29c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71zM67.53 368c21.22-27.97 44.42-74.33 44.53-159.42 0-.2-.06-.38-.06-.58 0-61.86 50.14-112 112-112s112 50.14 112 112c0 .2-.06.38-.06.58.11 85.1 23.31 131.46 44.53 159.42H67.53zM224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64z" />
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <>
                {isInstallationSelected && (
                  <Button
                    test="orderWithInstallButtonSticky"
                    onClick={orderWithInstall}
                    label={"С установкой"}
                  />
                )}
                {isShowAddToCart && (
                  <Button
                    test="addToCartButtonSticky"
                    type={
                      isInCart || isInstallationSelected ? "outlineGray" : ""
                    }
                    customStyles={
                      isInstallationSelected && !isInCart
                        ? styles.SecondaryButton
                        : undefined
                    }
                    onClick={addToCart}
                    label={isInCart ? "В корзине" : "В корзину"}
                    isLoading={isAddingToCart}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
